import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaTintSlash, FaArrowDown, FaCalendarAlt } from 'react-icons/fa';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement,
    LineElement, BarElement, Title, Tooltip, Filler, Legend
} from 'chart.js';

import AppLayout from '../../layouts/AppLayout';
import useAuth from '../../hooks/useAuth';
import useActiveCondominio from '../../hooks/useActiveCondominio';
import './styles/analytics.css';

ChartJS.register(
    CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Filler, Legend
);

const BlocoAnalyticsPage = () => {
    const { blocoId } = useParams();
    const navigate = useNavigate();

    const { user } = useAuth();
    const { activeCondominio, condominios } = useActiveCondominio();

    const getLocalISODate = (d = new Date()) => {
        const tzOffset = d.getTimezoneOffset() * 60000;
        return new Date(d.getTime() - tzOffset).toISOString().split('T')[0];
    };

    const [periodo, setPeriodo] = useState('30d');
    const [customDate, setCustomDate] = useState(getLocalISODate());
    const [analytics, setAnalytics] = useState(null);
    const [blocoNome, setBlocoNome] = useState('Bloco');
    const [loading, setLoading] = useState(true);

    // Buscar nome do bloco
    useEffect(() => {
        const fetchBlocoNome = async () => {
            if (!activeCondominio) return;
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
                const res = await axios.get(`${apiUrl}/api/reservatorios/blocos`, {
                    headers: { 'x-condominio-id': activeCondominio }
                });
                const bloco = res.data?.dados?.find(b => String(b.id) === String(blocoId));
                if (bloco) setBlocoNome(bloco.nome);
            } catch (err) { /* ignore */ }
        };
        fetchBlocoNome();
    }, [activeCondominio, blocoId]);

    useEffect(() => {
        const fetchAnalytics = async () => {
            if (!activeCondominio || !blocoId) return;

            setLoading(true);
            try {
                let dataInicio, dataFim;
                const hoje = new Date();
                dataFim = getLocalISODate(hoje);

                if (periodo === 'custom') {
                    dataInicio = customDate;
                    dataFim = customDate;
                } else if (periodo === '7d') {
                    const d7 = new Date();
                    d7.setDate(hoje.getDate() - 7);
                    dataInicio = getLocalISODate(d7);
                } else if (periodo === '30d') {
                    const d30 = new Date();
                    d30.setDate(hoje.getDate() - 30);
                    dataInicio = getLocalISODate(d30);
                }

                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
                const response = await axios.get(`${apiUrl}/api/reservatorios/blocos/${blocoId}/analytics`, {
                    headers: { 'x-condominio-id': activeCondominio },
                    params: { dataInicio, dataFim }
                });

                setAnalytics(response.data.dados);
            } catch (err) {
                console.error("Erro ao buscar analytics do bloco", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, [activeCondominio, blocoId, periodo, customDate]);

    // ====== FORMATAR LITROS ======
    const fmtLitros = (valor) => {
        if (valor == null || isNaN(valor)) return '0 L';
        if (valor >= 1000) return `${(valor / 1000).toFixed(1)}k L`;
        return `${Math.round(valor)} L`;
    };

    // ====== LINE CHART - Evolução Histórica Cúbica ======
    const chartData = {
        labels: analytics?.registros?.map(reg => {
            if (analytics.isAggregated) {
                const date = new Date(reg.timestamp);
                date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
                return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
            } else {
                return new Date(reg.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            }
        }) || [],
        datasets: [
            {
                label: 'Volume Total do Bloco (L)',
                data: analytics?.registros?.map(reg => reg.nivel_litros_total) || [],
                fill: true,
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return 'rgba(14, 165, 233, 0.08)';
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, 'rgba(14, 165, 233, 0.25)');
                    gradient.addColorStop(1, 'rgba(14, 165, 233, 0.0)');
                    return gradient;
                },
                borderColor: '#0ea5e9',
                tension: 0.4,
                pointRadius: analytics?.isAggregated ? 4 : 0,
                pointHoverRadius: 6,
                borderWidth: 2.5
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: (context) => {
                        return ` Volume: ${fmtLitros(context.parsed.y)}`;
                    }
                }
            },
            legend: { display: false }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (val) => fmtLitros(val)
                }
            },
            x: {
                grid: { display: false }
            }
        }
    };

    // ====== BAR CHART - Perfil de Consumo Horário Macro ======
    const hourlyLabels = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);
    const hourlyRawData = new Array(24).fill(0);
    if (analytics?.hourlyProfile) {
        analytics.hourlyProfile.forEach(h => {
            hourlyRawData[h.hora_do_dia] = h.nivel_litros_total;
        });
    }

    const hourlyData = {
        labels: hourlyLabels,
        datasets: [{
            label: 'Média de Volume (L) no Horário',
            data: hourlyRawData,
            backgroundColor: 'rgba(14, 165, 233, 0.6)',
            borderColor: 'rgba(14, 165, 233, 1)',
            borderWidth: 1,
            borderRadius: 4,
            barPercentage: 0.8
        }]
    };

    const hourlyOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => ` Média: ${fmtLitros(ctx.parsed.y)}`
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: { callback: (val) => fmtLitros(val) }
            },
            x: { grid: { display: false } }
        }
    };

    // ====== DATE PICKER LABEL ======
    const getCustomDateLabel = () => {
        const todayStr = getLocalISODate();
        if (customDate === todayStr) return "Hoje (Granular)";
        const d = new Date(customDate);
        d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
        return d.toLocaleDateString('pt-BR') + " (Granular)";
    };

    return (
        <AppLayout
            titulo={`Analytics - ${blocoNome}`}
            cargo={user?.cargo}
            condominios={condominios}
            activeCondominio={activeCondominio}
            onCondomínioChange={() => { }}
            isChecking={loading}
        >
            <div className="analytics-page-container">
                <div className="analytics-header">
                    <button className="back-btn" onClick={() => navigate('/reservatorios')}>
                        <FaArrowLeft /> Voltar para Monitoramento
                    </button>
                    <h2>Analytics do Bloco: {blocoNome}</h2>
                    <p className="section-subtitle" style={{ marginTop: '4px' }}>
                        Visão macro consolidando todos os tanques deste edifício em Litros absolutos.
                    </p>
                </div>

                <div className="analytics-controls">
                    <div className="filters-group">
                        <button
                            className={`filter-btn custom-date-btn ${periodo === 'custom' ? 'active' : ''}`}
                            onClick={() => setPeriodo('custom')}
                        >
                            <span>{getCustomDateLabel()}</span>
                            <div className="date-picker-icon-box">
                                <FaCalendarAlt />
                                <input
                                    type="date"
                                    className="analytics-date-picker-invisible"
                                    value={customDate}
                                    max={getLocalISODate()}
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            setCustomDate(e.target.value);
                                            setPeriodo('custom');
                                        }
                                    }}
                                />
                            </div>
                        </button>
                        <button className={`filter-btn ${periodo === '7d' ? 'active' : ''}`} onClick={() => setPeriodo('7d')}>Últimos 7 dias</button>
                        <button className={`filter-btn ${periodo === '30d' ? 'active' : ''}`} onClick={() => setPeriodo('30d')}>Últimos 30 dias (Média)</button>
                    </div>
                </div>

                {loading ? (
                    <div className="analytics-loading">Consolidando dados de todos os tanques do edifício...</div>
                ) : (
                    <>
                        <div className="analytics-dashboard-grid">
                            <div className="chart-main-card">
                                <h3 className="section-group-title">Evolução Histórica de Capacidade Cúbica</h3>
                                <p className="section-subtitle">Soma dos litros armazenados em todos os tanques do bloco ao longo do período selecionado</p>
                                <div className="chart-wrapper">
                                    {analytics?.registros?.length > 0 ? (
                                        <Line data={chartData} options={chartOptions} />
                                    ) : (
                                        <div className="empty-chart">Sem dados de leitura no período.</div>
                                    )}
                                </div>
                            </div>

                            <div className="highlights-sidebar">
                                <div className="highlight-card danger">
                                    <div className="highlight-icon"><FaTintSlash /></div>
                                    <div className="highlight-content">
                                        <span className="hl-label">Menor Volume Registrado</span>
                                        <span className="hl-value">
                                            {analytics?.highlights?.menorNivel ? fmtLitros(analytics.highlights.menorNivel.valor) : 'N/A'}
                                        </span>
                                        <span className="hl-sub">
                                            {analytics?.highlights?.menorNivel?.timestamp ? new Date(analytics.highlights.menorNivel.timestamp).toLocaleString('pt-BR') : '-'}
                                        </span>
                                    </div>
                                </div>

                                <div className="highlight-card warning">
                                    <div className="highlight-icon"><FaArrowDown /></div>
                                    <div className="highlight-content">
                                        <span className="hl-label">
                                            {analytics?.isAggregated ? "Maior Queda Diária de Volume" : "Maior Queda Registrada (Pico)"}
                                        </span>
                                        <span className="hl-value">
                                            {analytics?.highlights?.maiorQueda ? `-${fmtLitros(analytics.highlights.maiorQueda.valor)}` : 'N/A'}
                                        </span>
                                        <span className="hl-sub" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                                            {analytics?.highlights?.maiorQueda?.intervalo ? (
                                                analytics.isAggregated
                                                    ? `Entre ${new Date(analytics.highlights.maiorQueda.intervalo.de).toLocaleDateString('pt-BR')} e ${new Date(analytics.highlights.maiorQueda.intervalo.ate).toLocaleDateString('pt-BR')}`
                                                    : `Entre ${new Date(analytics.highlights.maiorQueda.intervalo.de).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} e ${new Date(analytics.highlights.maiorQueda.intervalo.ate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
                                            ) : '-'}
                                        </span>
                                    </div>
                                </div>

                                <div className="info-card">
                                    <h4>Visão Macro do Edifício</h4>
                                    <p>
                                        Os dados refletem a <strong>soma cúbica</strong> de todos os reservatórios cadastrados neste bloco.
                                        As métricas de consumo e armazenamento são calculadas em Litros absolutos,
                                        oferecendo uma visão real da capacidade hídrica do edifício.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="analytics-insights-section">
                            <div className="insights-grid insights-grid--single">
                                <div className="chart-main-card">
                                    <h3 className="section-group-title">Perfil Macro de Abastecimento (Horas)</h3>
                                    <p className="section-subtitle">Qual hora do dia o edifício retém mais ou menos litros?</p>
                                    <div className="chart-wrapper">
                                        {analytics?.hourlyProfile?.length > 0 ? (
                                            <Bar data={hourlyData} options={hourlyOptions} />
                                        ) : (
                                            <div className="empty-chart">Sem perfil detectável</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="chart-main-card table-wrapper-card">
                                <div className="table-header">
                                    <h3 className="section-group-title">Relatório de Auditoria de Água do Edifício</h3>
                                    <span className="table-badge">{analytics?.isAggregated ? 'Consolidação Diária' : 'Visão Sensorial (Granular)'}</span>
                                </div>
                                <div className="table-scroll-container">
                                    <table className="medicoes-table">
                                        <thead>
                                            <tr>
                                                <th>Data/Hora</th>
                                                <th>Volume Total (Litros)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {analytics?.registros?.slice().reverse().map((reg, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        {analytics.isAggregated
                                                            ? (() => {
                                                                const dt = new Date(reg.timestamp);
                                                                dt.setMinutes(dt.getMinutes() + dt.getTimezoneOffset());
                                                                return dt.toLocaleDateString('pt-BR');
                                                            })()
                                                            : new Date(reg.timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                                                        }
                                                    </td>
                                                    <td>
                                                        <span className="medicao-badge medicao-badge--normal">
                                                            {fmtLitros(reg.nivel_litros_total)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {analytics?.registros?.length === 0 && (
                                                <tr>
                                                    <td colSpan="2" style={{ textAlign: 'center', padding: '30px', color: 'var(--color-text-tertiary)' }}>Sem amostras registradas para esta janela de tempo.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </AppLayout>
    );
};

export default BlocoAnalyticsPage;
